import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownLeft, ArrowUpRight, Sparkles, Radio, Coins, Clock, CheckCircle2,
  XCircle, Hourglass, ArrowLeftRight, Loader2, Banknote, Heart,
} from 'lucide-react';
import { getWalletLedger } from '../api/wallet';
import { fmtCredits, creditsToEur } from '../lib/currency';
import { Button } from './ui/button';

// Phase 31 — Wallet Ledger UI.
// Reads from /api/wallet/ledger and presents a true bank-statement style
// list of Credits movements. Filters mirror the backend taxonomy.
//
// Phase 38 · Feb 2026 — accepts a `mode` prop that reshapes the surface
// for buyer-only users (customer).
// Phase F9.6 — labels moved to i18n (wallet.ledger.*) for IT/EN parity.

const FILTERS_CREATOR = [
  { key: 'all',        tkey: 'all' },
  { key: 'in',         tkey: 'in' },
  { key: 'out',        tkey: 'out' },
  { key: 'topup',      tkey: 'topup' },
  { key: 'live',       tkey: 'live' },
  { key: 'post',       tkey: 'post' },
  { key: 'tip',        tkey: 'tip' },
  { key: 'payout',     tkey: 'payout' },
  { key: 'refund',     tkey: 'refund' },
];

const FILTERS_BUYER = [
  { key: 'all',        tkey: 'all' },
  { key: 'topup',      tkey: 'topup_buyer' },
  { key: 'purchases',  tkey: 'purchases' },
  { key: 'refund',     tkey: 'refund_buyer' },
];

const KIND_ICON = {
  topup:        Coins,
  post_buy:     ArrowUpRight,
  live_buy:     Radio,
  tip_sent:     Heart,
  post_sale:    ArrowDownLeft,
  live_sale:    Radio,
  tip_received: Sparkles,
  payout:       Banknote,
  refund:       ArrowLeftRight,
  reverse:      XCircle,
};

const STATUS_CLS = {
  pending:           'bg-amber-500/15 text-amber-300',
  completed:         'bg-emerald-500/15 text-emerald-300',
  maturing:          'bg-amber-500/15 text-amber-300',
  available:         'bg-emerald-500/15 text-emerald-300',
  reversed:          'bg-red-500/15 text-red-300',
  refunded:          'bg-violet-500/15 text-violet-300',
  payout_requested:  'bg-amber-500/15 text-amber-300',
  payout_completed:  'bg-emerald-500/15 text-emerald-300',
  payout_rejected:   'bg-red-500/15 text-red-300',
  cancelled:         'bg-muted text-muted-foreground',
};

const STATUS_ICON = {
  pending:           Hourglass,
  maturing:          Hourglass,
  available:         CheckCircle2,
  completed:         CheckCircle2,
  payout_completed:  CheckCircle2,
  payout_rejected:   XCircle,
  reversed:          XCircle,
  refunded:          ArrowLeftRight,
  payout_requested:  Clock,
  cancelled:         XCircle,
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
};

// F9.6.1 — bilingual row descriptions rebuilt client-side from the
// structured fields (kind/direction/status/counterparty). The backend
// `description` string (Italian) is kept as a SAFE FALLBACK only.
const ADMIN_REASONS = ['refund', 'compensation', 'customer_support', 'correction', 'promo_bonus', 'contest', 'test', 'other'];

const ledgerDesc = (entry, t) => {
  const u = entry.counterparty_username;
  const at = u ? ` · @${u}` : '';
  switch (entry.kind) {
    case 'topup':        return t('ledger.desc.topup');
    case 'tip_sent':     return u ? t('ledger.desc.tip_sent_to', { u }) : t('ledger.desc.tip_sent');
    case 'tip_received': return u ? t('ledger.desc.tip_received_from', { u }) : t('ledger.desc.tip_received');
    case 'post_buy':     return t('ledger.desc.post_buy') + at;
    case 'live_buy':     return t('ledger.desc.live_buy') + at;
    case 'post_sale':    return t('ledger.desc.post_sale') + at;
    case 'live_sale':    return t('ledger.desc.live_sale') + at;
    case 'live_refund':
      return (entry.direction === 'out' ? t('ledger.desc.live_refund_issued') : t('ledger.desc.live_refund')) + at;
    case 'o2o_buy':      return t('ledger.desc.o2o_buy') + at;
    case 'o2o_sale':     return t('ledger.desc.o2o_sale') + at;
    case 'payout':
      if (entry.status === 'payout_completed') return t('ledger.desc.payout_completed');
      if (entry.status === 'payout_rejected') return t('ledger.desc.payout_rejected');
      return t('ledger.desc.payout_requested');
    case 'refund':
      return entry.status === 'refunded' ? t('ledger.desc.refund_done') : t('ledger.desc.refund_pending');
    case 'admin_credit':
    case 'admin_debit':
      return entry.reason && ADMIN_REASONS.includes(entry.reason)
        ? t(`ledger.desc.admin_reason.${entry.reason}`)
        : entry.description;
    default:
      return entry.description;
  }
};

const LedgerRow = ({ entry, showEur = true }) => {
  const { t } = useTranslation('wallet');
  const KindIcon = KIND_ICON[entry.kind] || Coins;
  const StatusIcon = STATUS_ICON[entry.status] || Hourglass;
  const statusCls = STATUS_CLS[entry.status] || STATUS_CLS.pending;
  const statusLabel = t(`ledger.status.${entry.status}`, { defaultValue: t('ledger.status.pending') });
  const isIn = entry.direction === 'in';
  const sign = entry.amount_credits > 0 ? '+' : entry.amount_credits < 0 ? '−' : '';
  const abs = Math.abs(entry.amount_credits || 0);

  return (
    <li
      className="flex items-start gap-3 px-5 py-4 hover:bg-card/40 transition-colors"
      data-testid={`ledger-row-${entry.id}`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          isIn ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 gold-text'
        }`}
      >
        <KindIcon className="h-5 w-5" strokeWidth={1.8} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-semibold text-foreground" data-testid={`ledger-row-desc-${entry.id}`}>
            {ledgerDesc(entry, t)}
          </p>
          <p
            className={`shrink-0 tabular-nums text-sm font-bold ${
              isIn ? 'text-emerald-300' : 'gold-text'
            }`}
            data-testid={`ledger-row-amount-${entry.id}`}
          >
            {sign}{fmtCredits(abs)}
          </p>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {fmtDateTime(entry.created_at)}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusCls}`}
            data-testid={`ledger-row-status-${entry.id}`}
          >
            <StatusIcon className="h-3 w-3" /> {statusLabel}
          </span>
          {showEur && (
            <span className="tabular-nums" data-testid={`ledger-row-eur-${entry.id}`}>
              ≈ €{creditsToEur(abs).toFixed(2)}
            </span>
          )}
          {entry.state_changed_at && entry.status !== 'pending' && (
            <span className="italic">{t('ledger.state_updated', { date: fmtDateTime(entry.state_changed_at) })}</span>
          )}
        </div>
      </div>
    </li>
  );
};

const FilterPills = ({ value, onChange, filters }) => {
  const { t } = useTranslation('wallet');
  return (
    <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-border" data-testid="ledger-filters">
      {filters.map((f) => (
        <button
          key={f.key}
          onClick={() => onChange(f.key)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            value === f.key
              ? 'gold-gradient text-black'
              : 'border border-border text-muted-foreground hover:border-amber-500/40 hover:text-foreground'
          }`}
          data-testid={`ledger-filter-${f.key}`}
        >
          {t(`ledger.filters.${f.tkey}`)}
        </button>
      ))}
    </div>
  );
};

const WalletLedger = ({ mode = 'creator' }) => {
  const { t } = useTranslation('wallet');
  const isBuyer = mode === 'buyer';
  const filters = isBuyer ? FILTERS_BUYER : FILTERS_CREATOR;
  const [filter, setFilter] = useState('all');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 25;

  const load = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const res = await getWalletLedger({ filter: f, page: p, pageSize: PAGE_SIZE });
      setEntries(res.entries);
      setTotal(res.total);
    } catch (_e) {
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter, page); }, [filter, page, load]);

  useEffect(() => {
    // Refresh when any wallet-impacting action happens elsewhere in the app.
    const h = () => load(filter, page);
    window.addEventListener('nymia:data-changed', h);
    return () => window.removeEventListener('nymia:data-changed', h);
  }, [filter, page, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section
      className="rounded-xl border border-border bg-card"
      data-testid="wallet-ledger"
      data-mode={mode}
    >
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <div>
          <h2 className="text-sm font-semibold" data-testid="ledger-title">
            {isBuyer ? t('ledger.title_buyer') : t('ledger.title_creator')}
          </h2>
          <p className="text-[11px] text-muted-foreground" data-testid="ledger-subtitle">
            {isBuyer
              ? t('ledger.subtitle_buyer')
              : t('ledger.subtitle_creator', { count: total })}
          </p>
        </div>
      </header>

      <FilterPills value={filter} onChange={(f) => { setFilter(f); setPage(1); }} filters={filters} />

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="ledger-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="px-5 py-16 text-center text-sm text-muted-foreground" data-testid="ledger-empty">
          {isBuyer ? t('ledger.empty_buyer') : t('ledger.empty_creator')}
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="ledger-list">
          {entries.map((e) => <LedgerRow key={e.id} entry={e} showEur={!isBuyer} />)}
        </ul>
      )}

      {totalPages > 1 && (
        <footer className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
          <span>{t('ledger.page_of', { page, total: totalPages })}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="ledger-prev-btn"
            >
              {t('ledger.prev')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="ledger-next-btn"
            >
              {t('ledger.next')}
            </Button>
          </div>
        </footer>
      )}
    </section>
  );
};

export default WalletLedger;
